import guide.GuideRegistry
import guide.GuideScreen
import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.browser.localStorage
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent

/**
 * Step-by-step contextual tour overlay.
 *
 * Usage:
 *   GuideOverlay(GuideScreen.TRANSACTIONS).start()
 *
 * Design note: DOM elements (backdrop strips, spotlight ring, bubble) are created
 * once when the tour starts and their **positions/content are updated** on each
 * step change. That lets CSS `transition` animate the spotlight sliding between
 * targets, and keeps step changes snappy (no tear-down/rebuild flicker).
 *
 * First-visit tracking: once the user opens or dismisses a tour for a screen, we
 * set `localStorage.wiz3r_guide_seen_<screen>=true` so the ⓘ button stops pulsing.
 */
// Top-level helper so pages can check demo mode without instantiating
// the overlay. Same source-of-truth as the instance-level demoMode flag
// below (URL query → sessionStorage fallback). Used by each page's
// nav-tour wiring to auto-click after the page renders.
fun isWiz3rDemoMode(): Boolean {
    val q = window.location.search
    val fromUrl = q.contains("demo=1") || q.contains("demo=true")
    if (fromUrl) window.sessionStorage.setItem("wiz3r_demo", "1")
    return fromUrl || window.sessionStorage.getItem("wiz3r_demo") == "1"
}

/**
 * If demo mode is active, schedule a programmatic click on the page's
 * tour-trigger button so the tour auto-starts after the data has had a
 * moment to load. Without this, the marketing-capture workflow still
 * required the user to manually click the 'i' button on every page.
 *
 * Each page calls this once during its setup phase (after the
 * nav-tour onclick handler is attached). The delay gives async data
 * fetches a window to populate — too eager and many anchors are
 * findVisibleTarget=null and get auto-skipped.
 *
 * Guarded by an id-level check: if a tour is already open (host
 * div in DOM) we skip, so a manual click on 'i' before the
 * timeout doesn't double-open.
 */
fun autoStartTourIfDemoMode(buttonSelector: String, delayMs: Int = 3000) {
    if (!isWiz3rDemoMode()) return
    // ?tour-manual-start=1 disables auto-start so Eric can deliver a
    // page-intro line to camera BEFORE the tour fires. He clicks the ⓘ
    // button when he's ready. Auto-advance within the tour still runs
    // per ?tour-auto=N.
    val q = window.location.search
    val manualStart = q.contains("tour-manual-start=1") ||
        window.sessionStorage.getItem("wiz3r_tour_manual_start") == "1"
    if (q.contains("tour-manual-start=1")) {
        window.sessionStorage.setItem("wiz3r_tour_manual_start", "1")
    }
    if (manualStart) return
    window.setTimeout({
        // Skip if a tour is already on screen (the host element has a
        // distinctive z-index: 20000 fixed wrapper).
        val tourAlreadyOpen = document.querySelectorAll("[data-wiz3r-guide-host]").length > 0
        if (tourAlreadyOpen) return@setTimeout
        (document.querySelector(buttonSelector) as? HTMLElement)?.click()
    }, delayMs)
}

/**
 * Emit an [INTRO] marker to the console when a page renders in demo
 * mode. The SRT generator picks these up so the intro caption ("This
 * is your dashboard…") gets a timestamp anchor in the final video.
 *
 * Idempotent per screen per session: called on every render but only
 * logs the first time each screen is seen. Prevents duplicate entries
 * from re-renders on the same page.
 *
 * The narration text itself is NOT emitted — the SRT script looks up
 * the screen key in demo_narration.md so the source of truth for the
 * intro copy stays in the markdown, not scattered across .kt files.
 */
private val emittedIntroScreens = mutableSetOf<String>()
fun emitDemoIntro(screen: String) {
    if (!isWiz3rDemoMode()) return
    if (screen in emittedIntroScreens) return
    emittedIntroScreens += screen
    val nowMs = js("Date.now()") as Double
    console.log("[INTRO t=${nowMs.toLong()} $screen]")
}

class GuideOverlay(
    private val screen: GuideScreen,
    private val onClose: (() -> Unit)? = null,
    /** Toggles the Wiz 3R variant of the Goals tour body. The caller knows
     *  whether the user has 3R-narrative cards visible at tour start time
     *  (see WebSavingsGoalsComponent for the detection). */
    wiz3rEnabled: Boolean = false,
) {
    private val steps = GuideRegistry.stepsFor(screen, wiz3rEnabled)
    private var index = 0
    private var lastAnchorId: String? = null

    // Demo-mode plumbing (?demo=1 / ?tour-auto=<ms>). Designed for the
    // marketing-video capture workflow:
    //   ?demo=1            → bigger bubble fonts (portrait readability),
    //                        chapter markers logged to console with
    //                        elapsed mm:ss timestamps so the editor can
    //                        ffmpeg-split a long recording on a single pass
    //   ?tour-auto=6000    → auto-advance every 6 s, no clicking required;
    //                        you talk over each step into the mic
    //
    // Both flags persist in sessionStorage on first sight so the user
    // doesn't have to re-paste the URL after each tab navigation. Closing
    // the browser tab clears them (sessionStorage is tab-scoped) — no
    // risk of demo mode leaking into a normal browsing session.
    private val demoMode: Boolean = run {
        val q = window.location.search
        val fromUrl = q.contains("demo=1") || q.contains("demo=true")
        if (fromUrl) window.sessionStorage.setItem("wiz3r_demo", "1")
        fromUrl || window.sessionStorage.getItem("wiz3r_demo") == "1"
    }
    private val autoAdvanceMs: Int = run {
        val q = window.location.search
        val fromUrl = Regex("""[?&]tour-auto=(\d+)""").find(q)?.groupValues?.get(1)?.toIntOrNull()
        if (fromUrl != null) window.sessionStorage.setItem("wiz3r_tour_auto", fromUrl.toString())
        fromUrl ?: window.sessionStorage.getItem("wiz3r_tour_auto")?.toIntOrNull() ?: 0
    }
    private var autoAdvanceTimer: Int? = null
    private val tourStartMs: Double = js("Date.now()") as Double

    // Persistent DOM. Created once on start(), positions + text updated on each render.
    private var host: HTMLElement? = null
    private var backdropTop: HTMLElement? = null
    private var backdropLeft: HTMLElement? = null
    private var backdropRight: HTMLElement? = null
    private var backdropBottom: HTMLElement? = null
    private var ring: HTMLElement? = null
    private var bubble: HTMLElement? = null
    private var bubbleTitle: HTMLElement? = null
    private var bubbleBody: HTMLElement? = null
    private var bubbleCounter: HTMLElement? = null
    private var bubbleBack: HTMLButtonElement? = null
    private var bubbleNext: HTMLButtonElement? = null

    private var onResize: ((Event) -> Unit)? = null
    private var onKey: ((Event) -> Unit)? = null

    fun start() {
        if (steps.isEmpty()) return
        markSeen(screen)
        buildDom()
        index = 0
        lastAnchorId = null

        onResize = { positionOnly() }
        onKey = { ev ->
            when ((ev as KeyboardEvent).key) {
                "Escape" -> close()
                "ArrowRight", "Enter" -> next()
                "ArrowLeft" -> prev()
            }
        }
        window.addEventListener("resize", onResize!!)
        document.addEventListener("keydown", onKey!!)

        renderStep()
    }

    private fun next() {
        if (index < steps.lastIndex) {
            index += 1
            renderStep()
        } else {
            close()
        }
    }

    private fun prev() {
        if (index > 0) {
            index -= 1
            renderStep()
        }
    }

    private fun close() {
        autoAdvanceTimer?.let { window.clearTimeout(it) }
        autoAdvanceTimer = null
        demoActionTimer?.let { window.clearTimeout(it) }
        demoActionTimer = null
        onResize?.let { window.removeEventListener("resize", it) }
        onKey?.let { document.removeEventListener("keydown", it) }
        host?.remove()
        host = null
        onClose?.invoke()
    }

    private fun buildDom() {
        val t = WebTheme.current
        val glassBg = if (t.isDark) "rgba(16, 22, 32, 0.96)" else "rgba(255, 255, 255, 0.96)"

        val h = document.createElement("div") as HTMLElement
        h.style.cssText = "position: fixed; inset: 0; z-index: 20000; pointer-events: none;"
        // Marker attribute used by autoStartTourIfDemoMode to avoid
        // double-opening a tour that's already on screen.
        h.setAttribute("data-wiz3r-guide-host", "1")
        document.body?.appendChild(h)
        host = h

        // Plain dim — no backdrop-filter. Blur on overlapping full-viewport elements was
        // forcing a compositor repaint of the entire page on each frame, which is what
        // felt like lag on Next.
        fun makeBackdrop(): HTMLElement {
            val d = document.createElement("div") as HTMLElement
            d.style.cssText = """
                position: fixed;
                background: rgba(6, 12, 20, 0.66);
                pointer-events: auto;
                z-index: 1;
            """.trimIndent()
            d.addEventListener("click", { close() })
            h.appendChild(d)
            return d
        }
        backdropTop = makeBackdrop()
        backdropLeft = makeBackdrop()
        backdropRight = makeBackdrop()
        backdropBottom = makeBackdrop()

        val r = document.createElement("div") as HTMLElement
        r.style.cssText = """
            position: fixed;
            border-radius: 10px;
            box-shadow:
                0 0 0 2px ${t.accent},
                0 0 0 1px rgba(255, 255, 255, 0.08) inset,
                0 0 16px rgba(0, 217, 255, 0.55),
                0 0 42px rgba(0, 217, 255, 0.30);
            pointer-events: none;
        """.trimIndent()
        h.appendChild(r)
        ring = r

        // Demo-mode bumps fonts ~35% and widens the bubble to ~420px so
        // tour text stays readable when the recording is downscaled into
        // a 9:16 portrait frame for TikTok/Reels.
        val bubbleWidthPx = if (demoMode) 420 else 320
        val titlePx = if (demoMode) 20 else 15
        val bodyPx = if (demoMode) 17 else 13
        val controlsPx = if (demoMode) 15 else 13

        val b = document.createElement("div") as HTMLElement
        b.style.cssText = """
            position: fixed;
            max-width: ${bubbleWidthPx}px;
            background: $glassBg;
            color: ${t.textPrimary};
            border: 1px solid rgba(0, 217, 255, 0.45);
            border-radius: 14px;
            padding: 14px 16px 12px;
            box-shadow:
                0 14px 40px rgba(0, 0, 0, 0.55),
                0 0 28px rgba(0, 217, 255, 0.25);
            pointer-events: auto;
            z-index: 2;
            touch-action: manipulation;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        """.trimIndent()
        h.appendChild(b)
        bubble = b

        val title = document.createElement("div") as HTMLElement
        title.style.cssText = "font-size: ${titlePx}px; font-weight: 600; margin-bottom: 6px;"
        b.appendChild(title)
        bubbleTitle = title

        val body = document.createElement("div") as HTMLElement
        body.style.cssText = "font-size: ${bodyPx}px; line-height: 1.5; color: ${t.textSecondary}; margin-bottom: 12px;"
        b.appendChild(body)
        bubbleBody = body

        val closeBtn = document.createElement("button") as HTMLButtonElement
        closeBtn.style.cssText = """
            position: absolute;
            top: 8px; right: 10px;
            background: transparent;
            border: none;
            color: ${t.textSecondary};
            font-size: 16px;
            cursor: pointer;
            padding: 2px 6px;
            line-height: 1;
        """.trimIndent()
        closeBtn.textContent = "\u00d7"
        closeBtn.title = StringKey.TOUR_CLOSE.tr()
        closeBtn.addEventListener("click", { close() })
        b.appendChild(closeBtn)

        val controls = document.createElement("div") as HTMLElement
        controls.style.cssText = "display: flex; align-items: center; gap: 8px;"
        b.appendChild(controls)

        val counter = document.createElement("span") as HTMLElement
        counter.style.cssText = "font-size: ${(controlsPx - 1)}px; color: ${t.textSecondary}; flex: 1;"
        controls.appendChild(counter)
        bubbleCounter = counter

        val backBtn = document.createElement("button") as HTMLButtonElement
        backBtn.style.cssText = """
            padding: 6px 12px;
            background: transparent;
            border: 1px solid ${t.border};
            border-radius: 6px;
            color: ${t.textPrimary};
            font-size: ${controlsPx}px;
            cursor: pointer;
        """.trimIndent()
        backBtn.textContent = StringKey.TOUR_BACK.tr()
        backBtn.addEventListener("click", { prev() })
        controls.appendChild(backBtn)
        bubbleBack = backBtn

        val nextBtn = document.createElement("button") as HTMLButtonElement
        nextBtn.style.cssText = """
            padding: 6px 14px;
            background: ${t.accent};
            border: none;
            border-radius: 6px;
            color: ${if (t.isDark) "#000" else "#fff"};
            font-size: ${controlsPx}px;
            font-weight: 600;
            cursor: pointer;
        """.trimIndent()
        nextBtn.addEventListener("click", { next() })
        controls.appendChild(nextBtn)
        bubbleNext = nextBtn

        // TikTok-style capture mode: when the tour is auto-driving itself
        // (demo=1 + tour-auto=N), suppress the entire instructional
        // bubble. Post-production captions carry the narration and the
        // element ring + backdrops handle "look here" visually. Keeping
        // the bubble on top of the captions would double-up text streams
        // in different tones/lengths and clutter the 9:16 frame.
        //
        // Fallback: demo=1 WITHOUT tour-auto (someone stepping through
        // manually) keeps the bubble so the Next button is reachable —
        // silently hiding both text AND navigation would be a foot-gun.
        if (demoMode && autoAdvanceMs > 0) {
            b.style.display = "none"
        }
    }

    /**
     * Step change: update text, maybe scroll, then position elements.
     */
    private fun renderStep() {
        val step = steps[index]
        bubbleTitle?.textContent = step.titleKey.tr()
        bubbleBody?.textContent = step.bodyKey.tr()
        bubbleCounter?.textContent = StringKey.TOUR_STEP_OF.tr()
            .replaceFirst("%s", (index + 1).toString())
            .replaceFirst("%s", steps.size.toString())
        bubbleBack?.style?.display = if (index > 0) "inline-block" else "none"
        bubbleNext?.textContent =
            if (index == steps.lastIndex) StringKey.TOUR_DONE.tr() else StringKey.TOUR_NEXT.tr()

        val target = findVisibleTarget(step.anchorId)
        if (target == null) {
            // Missing anchor — skip to the next step instead of stalling.
            if (index < steps.lastIndex) {
                index += 1
                renderStep()
            } else {
                close()
            }
            return
        }

        // Always scroll on anchor change — `position: sticky` targets can report
        // a misleading "fully visible" rect (stuck at top: 80 under the nav) that
        // doesn't actually place the element where the ring ends up. Forcing the
        // scroll centers it reliably.
        val anchorChanged = lastAnchorId != step.anchorId
        lastAnchorId = step.anchorId
        if (anchorChanged) {
            // 'instant' explicitly overrides any CSS `scroll-behavior: smooth`
            // further up the tree; 'auto' alone can still smooth-scroll.
            target.scrollIntoView(js("{behavior: 'instant', block: 'center'}"))
        }

        positionOnly()
        // Re-read the rect after the browser has settled the scroll + any sticky
        // layout (the left-panel `position: sticky` can report a stale rect on
        // the frame right after scrollIntoView).
        window.requestAnimationFrame {
            positionOnly()
        }

        // Demo-mode: chapter marker for ffmpeg-split. Format is stable so
        // scripts/chop_demo_video.sh and scripts/generate_srt.sh can
        // parse it deterministically.
        //
        // Two timestamps are emitted:
        //   MM:SS   — elapsed from TOUR start (human-readable, fine for
        //             a single-page recording).
        //   t=ms    — Date.now() absolute epoch ms. Required for
        //             multi-tour sessions (Dashboard → Goals → Studio):
        //             each new tour resets MM:SS to 00:00, but the
        //             video is continuous. The scripts diff the first
        //             chapter's t= against later t=s to get true
        //             video-relative time regardless of tour resets.
        if (demoMode) {
            val nowMs = js("Date.now()") as Double
            val elapsedSec = ((nowMs - tourStartMs) / 1000.0).toInt()
            val mm = (elapsedSec / 60).toString().padStart(2, '0')
            val ss = (elapsedSec % 60).toString().padStart(2, '0')
            val absMs = nowMs.toLong()
            console.log("[CHAPTER $mm:$ss ${screen.name}.${step.anchorId} t=$absMs] ${step.titleKey.tr()}")
        }

        // Auto-advance scheduler (?tour-auto=N). Cancel any pending tick
        // first so a manual click during the wait doesn't double-advance.
        autoAdvanceTimer?.let { window.clearTimeout(it) }
        autoAdvanceTimer = null
        if (autoAdvanceMs > 0) {
            autoAdvanceTimer = window.setTimeout({ next() }, autoAdvanceMs)
        }

        // Per-step demo dramatics (?demo=1 only). Currently one beat:
        // grid-canvas pulses every widget's resize handles AND fires a
        // CustomEvent the dashboard listens for ("wiz3r-demo-resize-
        // widget") to shrink-and-restore one specific widget. The
        // dashboard owns the actual Gridstack.update() call — keeps
        // tour code and dashboard code decoupled.
        applyStepDemoEffects(step.anchorId)
    }

    /** Set body class + dispatch DOM events that opt-in dashboard code
     *  listens for, scoped to specific demo-mode-only tour steps.
     *  Always strips prior step's effects first, so the body class
     *  doesn't leak past its anchor. */
    private fun applyStepDemoEffects(anchorId: String) {
        demoActionTimer?.let { window.clearTimeout(it) }
        demoActionTimer = null
        if (!demoMode) return
        when (anchorId) {
            "customize-toolbar" -> {
                // Auto-open the Customize panel as the bubble lands on
                // this step — camera catches the open animation in sync
                // with the narrator's "click here to customize" line.
                // Hold ~700 ms before opening so the bubble lands first
                // and the user's eye is already on the toolbar.
                demoActionTimer = window.setTimeout({
                    document.dispatchEvent(org.w3c.dom.events.Event("wiz3r-demo-open-customize"))
                }, 700)
            }
            "grid-canvas" -> {
                // Fire IMMEDIATELY so the dashboard handler enables
                // resize and adds gs-edit-active to the target widget
                // right when the bubble lands. The handler itself
                // waits another beat before starting the animated
                // resize — see performGridCanvasDemoResize().
                document.dispatchEvent(org.w3c.dom.events.Event("wiz3r-demo-resize-widget"))
            }
            "layouts-switch" -> {
                // Close the Customize panel first so the chips bar is
                // unobscured and the camera lands on a clean toolbar.
                // The panel was opened during the customize-toolbar
                // step; the save + lock steps used the panel content;
                // for the switch demo (and everything after) we don't
                // need the panel open.
                document.dispatchEvent(org.w3c.dom.events.Event("wiz3r-demo-close-customize"))
                // Then programmatically click the other pinned-layout
                // chip after a beat so the user sees the dashboard
                // re-render into a different arrangement. Narrator's
                // "watch this" line lands as the swap fires.
                demoActionTimer = window.setTimeout({
                    document.dispatchEvent(org.w3c.dom.events.Event("wiz3r-demo-switch-layout"))
                }, 1500)
            }
        }
    }

    private var demoActionTimer: Int? = null

    /**
     * Re-layout without changing step text. Cheap enough to call from scroll/resize.
     */
    /**
     * Pick the last element with a non-zero rect for a given anchor id.
     * When the dashboard toggles between Standard and Grid layouts, a stale/hidden
     * element with the same `data-guide` can still be in the DOM; `querySelector`
     * would pick it first and the ring would draw at 0×0. Skipping zero-size
     * matches dodges that.
     */
    private fun findVisibleTarget(anchorId: String): HTMLElement? {
        val matches = document.querySelectorAll("[data-guide=\"$anchorId\"]")
        var best: HTMLElement? = null
        for (i in 0 until matches.length) {
            val el = matches.item(i) as? HTMLElement ?: continue
            val r = el.getBoundingClientRect()
            if (r.width > 0 && r.height > 0) best = el
        }
        return best
    }

    private fun positionOnly() {
        val step = steps.getOrNull(index) ?: return
        val target = findVisibleTarget(step.anchorId)
        val h = host ?: return
        if (target == null) return
        val rect = target.getBoundingClientRect()
        val padding = 6.0
        val vpW = window.innerWidth.toDouble()
        val vpH = window.innerHeight.toDouble()
        val top = (rect.top - padding).coerceAtLeast(0.0)
        val left = (rect.left - padding).coerceAtLeast(0.0)
        val right = (rect.right + padding).coerceAtMost(vpW)
        val bottom = (rect.bottom + padding).coerceAtMost(vpH)

        backdropTop?.style?.apply {
            this.top = "0px"; this.left = "0px"
            width = "${vpW}px"; height = "${top}px"
        }
        backdropLeft?.style?.apply {
            this.top = "${top}px"; this.left = "0px"
            width = "${left}px"; height = "${bottom - top}px"
        }
        backdropRight?.style?.apply {
            this.top = "${top}px"; this.left = "${right}px"
            width = "${vpW - right}px"; height = "${bottom - top}px"
        }
        backdropBottom?.style?.apply {
            this.top = "${bottom}px"; this.left = "0px"
            width = "${vpW}px"; height = "${vpH - bottom}px"
        }
        ring?.style?.apply {
            this.top = "${top}px"; this.left = "${left}px"
            width = "${right - left}px"; height = "${bottom - top}px"
        }

        val bubbleEl = bubble ?: return
        // Pin the bubble in the lower portion of the viewport. Centered placement (the
        // previous behavior) often landed on top of what the step was pointing at,
        // especially on mobile where the spotlighted element is usually in the upper half.
        // If the spotlighted target sits near the bubble's region and would be covered,
        // fall back to placing the bubble above the target.
        //
        // Mobile pins to the bottom (small viewport, every pixel matters); desktop sits
        // around the 3/4 line so it doesn't feel banished to the corner of a wide window.
        //
        // Why CSS `bottom` instead of computed `top`: iOS Safari fires resize events when
        // the URL bar collapses/expands during scroll. Recomputing `top = vpH - h - 16`
        // each time made the bubble jump under the user's finger between touchstart and
        // touchend — taps got interpreted as scrolls and Back/Next felt unresponsive.
        // CSS `bottom` tracks the viewport bottom natively, no JS recompute on resize.
        val bubbleRect = bubbleEl.getBoundingClientRect()
        val margin = 16.0
        val isMobile = vpW < 768
        val bottomCss = if (isMobile) "${margin}px" else "25vh"
        val bottomMarginPx = if (isMobile) margin else (vpH * 0.25)
        val bLeft = ((vpW - bubbleRect.width) / 2.0).coerceAtLeast(margin)
        val bottomBubbleTop = vpH - bubbleRect.height - bottomMarginPx
        val targetWouldOverlap = rect.bottom > bottomBubbleTop - margin
        if (targetWouldOverlap) {
            val aboveTop = rect.top - margin - bubbleRect.height
            if (aboveTop >= margin) {
                bubbleEl.style.top = "${aboveTop}px"
                bubbleEl.style.bottom = "auto"
            } else {
                bubbleEl.style.bottom = bottomCss
                bubbleEl.style.top = "auto"
            }
        } else {
            bubbleEl.style.bottom = bottomCss
            bubbleEl.style.top = "auto"
        }
        bubbleEl.style.left = "${bLeft}px"
    }

    companion object {
        private const val STYLES_ID = "wiz3r-guide-styles"

        private fun keyFor(screen: GuideScreen) = "wiz3r_guide_seen_${screen.name}"

        fun hasSeen(screen: GuideScreen): Boolean = localStorage.getItem(keyFor(screen)) == "true"

        fun markSeen(screen: GuideScreen) {
            runCatching { localStorage.setItem(keyFor(screen), "true") }
        }

        /**
         * Inject the `.wiz3r-guide-halo` pulse keyframes once per page load. The ⓘ
         * button applies this class on first-visit so it catches the eye until the
         * user opens or dismisses the tour.
         */
        fun ensureStylesInstalled() {
            if (document.getElementById(STYLES_ID) != null) return
            val style = document.createElement("style") as HTMLElement
            style.id = STYLES_ID
            style.textContent = """
                @keyframes wiz3rGuideHaloPulse {
                    0%, 100% { box-shadow: 0 0 0 0 rgba(0, 217, 255, 0.55); }
                    50%      { box-shadow: 0 0 0 8px rgba(0, 217, 255, 0); }
                }
                .wiz3r-guide-halo {
                    animation: wiz3rGuideHaloPulse 1.6s ease-in-out infinite;
                }

                /* Fake cursor that "drags" the SE corner of the target
                 * widget during the grid-canvas resize beat. The
                 * dashboard adds gs-edit-active to the target widget
                 * (Gridstack's built-in affordance that triggers the
                 * existing handle-visibility CSS in app.html) so the
                 * widget's own handles light up the standard way —
                 * no extra CSS overrides needed; the cursor follows
                 * the SE corner so the camera knows where to look. */
                .wiz3r-demo-cursor {
                    /* Child of the target .grid-stack-item, anchored
                     * to its SE corner. As Gridstack animates the
                     * widget's width/height via CSS transitions, the
                     * cursor's bottom/right offsets keep it glued to
                     * the corner — no JS tracking, perfect sync. */
                    position: absolute;
                    width: 32px; height: 32px;
                    bottom: -6px;
                    right: -6px;
                    pointer-events: none;
                    /* High z-index relative to the widget's stacking
                     * context so the cursor sits above the widget's
                     * own content (chart SVG, etc.). */
                    z-index: 100;
                    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%2300d9ff' stroke='white' stroke-width='1.5'><path d='M5 3l14 7-6 2-2 6z'/></svg>");
                    background-repeat: no-repeat;
                    background-size: contain;
                    filter: drop-shadow(0 2px 8px rgba(0,217,255,0.8));
                }
            """.trimIndent()
            document.head?.appendChild(style)
        }
    }
}
